/**
 * The record's sibling timeline in plain words (13-25), pure. `components/record/SiblingTimeline.tsx` renders it.
 *
 * - **One group per save.** Rows that share the base of their `[a:<id>]` / `[a:<id>.<n>]` tag (D-13-20-3) were
 *   written by one save, so they sit under one heading: the verb as a person reads it (`Triage`, `Lodged`,
 *   `Cancel`…), read from the row `Title` (`<verb> <ref>: …`, D-13-20-11), else the entry's own label. A row with no
 *   tag is a group of its own.
 * - **One line per row.** A `StateChange` row reads "Status changed from New to Actioning" with the field set's
 *   label and the value in words (days `D MMM YYYY`, instants with the Melbourne time); any other row reads its
 *   `LogKind` (or `EntryType`) label and its Note, with the `[a:<id>]` tag stripped for display.
 * - **Newest first** (groups), each group's rows in the order they were written.
 *
 * The `LogKind` labels are D-13-25-3. A test checks they cover the live `LogKind` set exactly.
 */

import { fieldOf } from '../config/fieldset/registerFields';
import { auditField } from '../config/fieldset/internalAudits';
import type { AuditEntryType } from '../types/qms';
import type { RecordRegister } from '../types/records';
import type { SiblingRow } from '../services/recordService';
import { CLOSURE_LABELS } from './recordClosure';
import { VERB_LABELS } from './recordVerbs';
import { AUDIT_VERB_WORDS } from './auditVerbs';
import { displayValue } from './verbForm';

/** Each `EntryType` in words. */
export const ENTRY_TYPE_LABELS: Readonly<Record<AuditEntryType, string>> = {
  StateChange: 'Change',
  InvestigationStep: 'Investigation step',
  ActionTaken: 'Action taken',
  Note: 'Note',
};

/** Each live `LogKind` in words (D-13-25-3; FIELDSET §3.4.1). */
export const LOG_KIND_LABELS: Readonly<Record<string, string>> = {
  Acknowledgement: 'Acknowledgement',
  AuditMeeting: 'Audit meeting',
  Communication: 'Outcome communicated',
  Consultation: 'Consultation',
  Disclosure: 'Disclosure',
  InformationRequest: 'Regulator asked for information',
  Notification: 'Notification',
  ProceduralFairness: 'Procedural fairness',
  ProgressUpdate: 'Progress update',
  RegulatorDecision: 'Regulator decision',
  RegulatorRequirement: 'Regulator requirement',
  ReprisalConcern: 'Reprisal concern',
  Review: 'Review',
  RTW: 'Return to work',
  SitePreservation: 'Site preserved',
  Support: 'Support offered',
};

/** The verb at the start of a row `Title`, in words. `lodge` reads "Lodged"; 13-32 adds the audit verbs. */
const RECORD_VERB_WORDS: Readonly<Record<string, string>> = { lodge: 'Lodged', ...VERB_LABELS, ...CLOSURE_LABELS };
/** The list-less reading (and Internal Audits'): the audit verbs win a shared key. */
const VERB_WORDS: Readonly<Record<string, string>> = { ...RECORD_VERB_WORDS, ...AUDIT_VERB_WORDS };
/**
 * A register's trail (13.2-03): its own verbs win a shared key, so an incident's `complete` (the CM's report) never
 * reads as the Internal Audits `complete` ("Audit completed").
 */
const REGISTER_VERB_WORDS: Readonly<Record<string, string>> = { ...AUDIT_VERB_WORDS, ...RECORD_VERB_WORDS };

/**
 * 13-32: whose trail a timeline reads: one of the five registers, or the Internal Audits list (its own field set,
 * `config/fieldset/internalAudits.ts`).
 */
export type TimelineList = RecordRegister | 'internalAudits';

function labelSpecOf(list: TimelineList, column: string): { label: string; kind: string } | undefined {
  return list === 'internalAudits' ? auditField(column) : fieldOf(list, column);
}

const TAG = /\s*\[a:([^\]\s]+)\]/g;

/** The `[a:<id>]` tag ids in a Note. */
export function tagsOf(note: string | null | undefined): string[] {
  if (!note) return [];
  return [...note.matchAll(TAG)].map((m) => m[1]);
}

/** The save a tag belongs to: `aud-1.2` → `aud-1` (D-13-20-3). */
export function saveIdOf(tag: string): string {
  return tag.replace(/\.\d+$/, '');
}

/** The Note with every `[a:<id>]` tag removed, trimmed; `''` when nothing is left. */
export function stripTags(note: string | null | undefined): string {
  return (note ?? '').replace(TAG, '').replace(/[ \t]+\n/g, '\n').trim();
}

/**
 * The verb that wrote a row, in words, from its `Title`; `null` when the title names no known verb. `list` (13.2-03)
 * says whose trail it is, which settles a key two lists share (`complete`).
 */
export function verbWordsOf(title: string | null | undefined, list?: TimelineList): string | null {
  // 13-32: the Internal Audits carry-in's own rows (`AUD-1 carried in from Centro`, `AUD-1 DueOn updated from Centro`).
  if (/\bcarried in from Centro\b/i.test(title ?? '')) return 'Carried in from Centro';
  if (/\bupdated from Centro\b/i.test(title ?? '')) return 'Updated from Centro';
  const first = (title ?? '').trim().split(/\s+/)[0] ?? '';
  const words = list === undefined || list === 'internalAudits' ? VERB_WORDS : REGISTER_VERB_WORDS;
  return Object.prototype.hasOwnProperty.call(words, first) ? words[first] : null;
}

/** A non-`StateChange` row's label: its `LogKind`, else its `EntryType`. */
export function entryLabelOf(row: Pick<SiblingRow, 'entryType' | 'logKind'>): string {
  if (row.logKind) return LOG_KIND_LABELS[row.logKind] ?? row.logKind;
  return row.entryType ? (ENTRY_TYPE_LABELS[row.entryType] ?? row.entryType) : 'Entry';
}

/** A `StateChange` row as a sentence: "Severity changed from No rating to Moderate", "… set to …", "… cleared". */
export function changeSentence(register: TimelineList, row: Pick<SiblingRow, 'fieldChanged' | 'oldValue' | 'newValue'>): string {
  const column = row.fieldChanged ?? '';
  const spec = labelSpecOf(register, column);
  const label = spec?.label ?? (column || 'A field');
  const oldBlank = (row.oldValue ?? '').trim() === '';
  const newBlank = (row.newValue ?? '').trim() === '';
  if (oldBlank && newBlank) return `${label} recorded as blank`;
  if (oldBlank) return `${label} set to ${displayValue(spec, row.newValue)}`;
  if (newBlank) return `${label} cleared (was ${displayValue(spec, row.oldValue)})`;
  return `${label} changed from ${displayValue(spec, row.oldValue)} to ${displayValue(spec, row.newValue)}`;
}

/**
 * What a `StateChange` Note adds beyond the change itself: the Note less its "<Verb> by <upn>." head and its tag
 * (a reason, "Client not on the client lists."). `''` when nothing.
 */
export function changeDetail(note: string | null | undefined): string {
  return stripTags(note).replace(/^[^\n]*?\bby\s+\S+@\S+?\.(?=\s|$)\s*/i, '').trim();
}

export interface TimelineLine {
  /** The sibling row id. */
  id: number;
  kind: 'change' | 'entry';
  /** The sentence (a change) or the entry's label. */
  label: string;
  /** A change's detail, or an entry's Note (tag stripped, line breaks kept); `''` when none. */
  text: string;
}

export interface TimelineGroup {
  /** Stable key: the save id, else the first row's id. */
  key: string;
  heading: string;
  /** Who wrote the first row: `ActorUPN`, else the item's Author. */
  actorUpn: string | null;
  /** When (the first row's `EntryTimestamp`, else its Created). */
  at: string | null;
  lines: TimelineLine[];
}

function timeOf(r: SiblingRow): number {
  const t = Date.parse(r.entryTimestamp ?? r.created ?? '');
  return Number.isNaN(t) ? 0 : t;
}

function lineOf(register: TimelineList, r: SiblingRow): TimelineLine {
  if (r.entryType === 'StateChange') {
    return { id: r.id, kind: 'change', label: changeSentence(register, r), text: changeDetail(r.note) };
  }
  return { id: r.id, kind: 'entry', label: entryLabelOf(r), text: stripTags(r.note) };
}

/** The timeline: one group per save, newest first. */
export function buildTimeline(register: TimelineList, rows: readonly SiblingRow[]): TimelineGroup[] {
  const sorted = [...rows].sort((a, b) => timeOf(a) - timeOf(b) || a.id - b.id);
  const groups: Array<TimelineGroup & { saveId: string | null }> = [];
  for (const r of sorted) {
    const tag = tagsOf(r.note)[0];
    const saveId = tag ? saveIdOf(tag) : null;
    const last = groups.at(-1);
    if (last && saveId !== null && last.saveId === saveId) {
      last.lines.push(lineOf(register, r));
      continue;
    }
    const words = verbWordsOf(r.title, register);
    groups.push({
      key: saveId ?? `row-${r.id}`,
      saveId,
      heading: words ?? (r.entryType === 'StateChange' ? 'Change' : entryLabelOf(r)),
      actorUpn: r.actorUpn ?? r.authorEmail ?? null,
      at: r.entryTimestamp ?? r.created ?? null,
      lines: [lineOf(register, r)],
    });
  }
  return groups.reverse().map((g) => ({ key: g.key, heading: g.heading, actorUpn: g.actorUpn, at: g.at, lines: g.lines }));
}

/** Every person a timeline names (for one display-name read), lower-cased, de-duplicated. */
export function timelineUpns(groups: readonly TimelineGroup[]): string[] {
  return [...new Set(groups.map((g) => (g.actorUpn ?? '').trim().toLowerCase()).filter((u) => u.includes('@')))];
}
